/**
 * Client CRM — full-page workspace (replaces the old drawer-based Clients tab).
 * Left rail: client list. Main: profile header, stats, About/Preferences,
 * unified activity timeline, ledger + upcoming trips.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { AttachmentsSection } from '../FileAttachments';
import { BookingDatabase, DocumentsPanel } from './Client360Records';
import {
  Search, Users, Crown, Building2, Phone, Mail, Calendar, FileText, Receipt,
  MessageSquare, StickyNote, DollarSign, Pencil, Check, X, Send, Plane, FileDown, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt$ = (n) => `$${Number(n || 0).toLocaleString()}`;
const SEGMENTS = [
  { key: 'all', label: 'All' }, { key: 'active', label: 'Active' }, { key: 'vip', label: 'VIP' },
  { key: 'corporate', label: 'Corporate' }, { key: 'consulting', label: 'Consulting' },
];
const TL_META = {
  booking: { icon: Calendar, color: 'text-blue-700 bg-blue-50 border-blue-200', label: 'Booking' },
  quote: { icon: FileText, color: 'text-amber-700 bg-amber-50 border-amber-200', label: 'Quote' },
  invoice: { icon: Receipt, color: 'text-purple-700 bg-purple-50 border-purple-200', label: 'Invoice' },
  sms: { icon: MessageSquare, color: 'text-green-700 bg-green-50 border-green-200', label: 'SMS' },
  note: { icon: StickyNote, color: 'text-gray-700 bg-gray-50 border-gray-200', label: 'Note' },
  ledger: { icon: DollarSign, color: 'text-red-700 bg-red-50 border-red-200', label: 'Ledger' },
};

const keyOf = (c) => c.client_id || c.client_key;

export default function ClientCRM() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [segment, setSegment] = useState('all');
  const [searchParams, setSearchParams] = useSearchParams();
  const activeKey = searchParams.get('client');
  const setActiveKey = (k) => setSearchParams(k ? { tab: 'clients', client: k } : { tab: 'clients' });
  const [data, setData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const loadList = useCallback(async () => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (segment !== 'all') params.set('segment', segment);
    const r = await fetch(`${API}/api/command-center/clients?${params.toString()}`);
    const d = await r.json();
    setClients(d.clients || []);
  }, [search, segment]);
  useEffect(() => { const t = setTimeout(loadList, 250); return () => clearTimeout(t); }, [loadList]);

  const loadDetail = useCallback(async (key) => {
    if (!key) return;
    setDetailLoading(true);
    setNotFound(false);
    try {
      const r = await fetch(`${API}/api/command-center/clients/360/${encodeURIComponent(key)}`);
      if (r.ok) setData(await r.json());
      else { setData(null); setNotFound(true); }
    } catch { toast.error('Load failed'); setData(null); setNotFound(true); }
    setDetailLoading(false);
  }, []);
  useEffect(() => { loadDetail(activeKey); }, [activeKey, loadDetail]);

  return (
    <div data-testid="client-crm" className="flex flex-col lg:flex-row gap-4" style={{ minHeight: '70vh' }}>
      {/* ── Left rail: client list ── */}
      <aside className="w-full lg:w-[300px] flex-shrink-0 bg-white border border-gray-200 flex flex-col max-h-[40vh] lg:max-h-[80vh]">
        <div className="p-3 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients…" className="pl-8 h-9 text-xs" data-testid="crm-search" />
          </div>
          <div className="flex gap-1 mt-2 flex-wrap">
            {SEGMENTS.map(s => (
              <button key={s.key} onClick={() => setSegment(s.key)} className={`text-[9px] font-bold uppercase tracking-wider px-2 py-1 border transition-colors ${segment === s.key ? 'bg-black text-white border-black' : 'bg-white text-gray-500 border-gray-200 hover:border-black'}`} data-testid={`crm-segment-${s.key}`}>{s.label}</button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="crm-client-list">
          {clients.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-8">No clients match.</p>
          ) : clients.map(c => {
            const k = keyOf(c);
            const active = activeKey === k;
            return (
              <button key={k} onClick={() => setActiveKey(k)} className={`w-full text-left px-3 py-2.5 border-b border-gray-50 flex items-center gap-2 transition-colors ${active ? 'bg-red-50 border-l-2 border-l-red-600' : 'hover:bg-gray-50'}`} data-testid={`crm-client-${c.client_key}`}>
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-700 to-black text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                  {(c.name || '?').slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold truncate">{c.name || c.phone || '(Unnamed)'}</p>
                  <p className="text-[10px] text-gray-400 truncate">{c.company || c.phone || c.email || '—'}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[11px] font-black">{fmt$(c.total_spent || 0)}</p>
                  {c.tier && <span className="text-[8px] uppercase bg-amber-100 text-amber-700 px-1 py-0.5 rounded-full font-bold">{c.tier}</span>}
                </div>
              </button>
            );
          })}
        </div>
        <div className="p-2 border-t border-gray-100 text-center text-[10px] text-gray-400">{clients.length} clients</div>
      </aside>

      {/* ── Main workspace ── */}
      <main className="flex-1 min-w-0">
        {!activeKey ? (
          <div className="h-full min-h-[400px] bg-white border border-gray-200 flex flex-col items-center justify-center text-center p-8" data-testid="crm-empty">
            <Users className="h-10 w-10 text-gray-200 mb-3" />
            <p className="text-sm font-bold text-gray-500">Select a client to open their full CRM profile</p>
            <p className="text-xs text-gray-400 mt-1">Contact info · preferences · timeline · balances — all in one workspace</p>
          </div>
        ) : notFound ? (
          <div className="h-full min-h-[400px] bg-white border border-gray-200 flex flex-col items-center justify-center text-center p-8" data-testid="crm-not-found">
            <Users className="h-10 w-10 text-gray-200 mb-3" />
            <p className="text-sm font-bold text-gray-600">No client record found for “{activeKey}”</p>
            <p className="text-xs text-gray-400 mt-1 mb-4">This contact hasn't been saved as a client yet.</p>
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 h-8 text-xs"
              data-testid="crm-create-client-btn"
              onClick={async () => {
                try {
                  const r = await fetch(`${API}/api/command-center/clients/360/${encodeURIComponent(activeKey)}`, {
                    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name: activeKey.includes('@') || /^\d+$/.test(activeKey) ? '' : activeKey, phone: /^\d{10}$/.test(activeKey) ? activeKey : '', email: activeKey.includes('@') ? activeKey : '' }),
                  });
                  if (!r.ok) throw new Error('Create failed');
                  toast.success('Client record created');
                  loadDetail(activeKey); loadList();
                } catch (e) { toast.error(e.message); }
              }}
            >Create Client Record</Button>
          </div>
        ) : detailLoading || !data ? (
          <div className="h-full min-h-[400px] bg-white border border-gray-200 flex items-center justify-center">
            <p className="text-sm text-gray-400">Loading profile…</p>
          </div>
        ) : (
          <ClientWorkspace data={data} clientKey={activeKey} reload={() => { loadDetail(activeKey); loadList(); }} />
        )}
      </main>
    </div>
  );
}

function ClientWorkspace({ data, clientKey, reload }) {
  const p = data.profile || {};
  const s = data.stats || {};
  const [editing, setEditing] = useState(false);
  const [statementSending, setStatementSending] = useState(false);

  const emailStatement = async () => {
    setStatementSending(true);
    try {
      const r = await fetch(`${API}/api/command-center/clients/360/${encodeURIComponent(clientKey)}/statement/email`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Send failed');
      toast.success(d.message || 'Statement emailed');
    } catch (e) { toast.error(e.message); }
    setStatementSending(false);
  };
  const [form, setForm] = useState({});
  const [composer, setComposer] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  const [ledgerForm, setLedgerForm] = useState({ type: 'payment', amount: '', description: '' });
  const [tagInput, setTagInput] = useState('');

  const startEdit = () => {
    setForm({
      name: p.name || '', phone: p.phone || '', email: p.email || '', company: p.company || '',
      tier: p.tier || '', notes: p.notes || '',
      preferences: { drink: p.preferences?.drink || '', music: p.preferences?.music || '', allergies: p.preferences?.allergies || '', seating: p.preferences?.seating || '' },
    });
    setEditing(true);
  };

  const patch = async (payload) => {
    const r = await fetch(`${API}/api/command-center/clients/360/${encodeURIComponent(clientKey)}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    });
    if (!r.ok) throw new Error('Save failed');
  };

  const saveEdit = async () => {
    try { await patch(form); toast.success('Profile updated'); setEditing(false); reload(); }
    catch (e) { toast.error(e.message); }
  };

  const addTag = async () => {
    const t = tagInput.trim();
    if (!t) return;
    try { await patch({ tags: [...(p.tags || []), t] }); setTagInput(''); reload(); }
    catch (e) { toast.error(e.message); }
  };

  const removeTag = async (t) => {
    try { await patch({ tags: (p.tags || []).filter(x => x !== t) }); reload(); }
    catch (e) { toast.error(e.message); }
  };

  const logNote = async () => {
    if (!composer.trim()) return;
    const r = await fetch(`${API}/api/command-center/clients/360/${encodeURIComponent(p.client_id || clientKey)}/note`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: composer.trim() }),
    });
    if (r.ok) { toast.success('Note logged'); setComposer(''); reload(); }
    else toast.error('Failed');
  };

  const sendSms = async () => {
    if (!composer.trim() || !p.phone) { toast.error('Message + client phone required'); return; }
    setSendingSms(true);
    try {
      const r = await fetch(`${API}/api/command-center/quo/send-sms`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: p.phone, message: composer.trim() }),
      });
      const d = await r.json();
      if (d.success) { toast.success('SMS sent'); setComposer(''); reload(); }
      else throw new Error('SMS failed');
    } catch (e) { toast.error(e.message); }
    setSendingSms(false);
  };

  const addLedger = async () => {
    const amount = Number(ledgerForm.amount);
    if (!amount) { toast.error('Amount required'); return; }
    const r = await fetch(`${API}/api/ops/ledger`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...ledgerForm, amount, client_phone: p.phone, client_email: p.email, client_name: p.name }),
    });
    if (r.ok) { toast.success('Ledger entry added'); setLedgerForm({ type: 'payment', amount: '', description: '' }); reload(); }
    else toast.error('Failed');
  };

  // Unified timeline
  const timeline = [
    ...(data.bookings || []).map(b => ({ type: 'booking', date: b.event_date, title: `${b.vehicle || 'Trip'} — ${fmt$(b.total)}`, sub: [b.pickup_time, b.pickup_address, b.flight_number ? `✈ ${b.flight_number}` : '', b.is_farmout ? `Farm-out: ${b.farmout_partner}` : ''].filter(Boolean).join(' · '), badge: b.cc_status || b.status })),
    ...(data.quotes || []).map(q => ({ type: 'quote', date: q.created_at, title: `Quote ${fmt$(q.grand_total || q.total || 0)} — ${q.service_type || q.vehicle || ''}`, sub: q.event_date, badge: q.status })),
    ...(data.invoices || []).map(i => ({ type: 'invoice', date: i.created_at, title: `Invoice ${fmt$(i.subtotal || 0)} — ${i.service_description || ''}`, badge: i.status })),
    ...(data.communications || []).map(c => ({ type: 'sms', date: c.created_at, title: c.body || c.message || '', badge: c.direction })),
    ...(p.notes_log || []).map(n => ({ type: 'note', date: n.created_at, title: n.text, badge: n.author })),
    ...(data.ledger || []).map(l => ({ type: 'ledger', date: l.created_at, title: `${l.type === 'payment' ? '−' : '+'}${fmt$(l.amount)} — ${l.description}`, badge: l.type })),
  ].filter(t => t.date).sort((a, b) => String(b.date).localeCompare(String(a.date)));

  const upcoming = (data.bookings || []).filter(b => (b.event_date || '') >= new Date().toISOString().slice(0, 10) && !['cancelled', 'deleted'].includes(b.cc_status)).slice(0, 5);
  const prefs = p.preferences || {};
  const hasPrefs = prefs.drink || prefs.music || prefs.allergies || prefs.seating;

  return (
    <div data-testid="crm-workspace">
      {/* Header */}
      <div className="bg-white border border-gray-200 p-4 mb-4 border-t-2 border-t-red-600">
        <p className="text-[9px] uppercase tracking-[0.3em] text-red-600 font-bold mb-2">ACE · Client Concierge File</p>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-14 w-14 rounded-full bg-gradient-to-br from-gray-700 to-black text-white text-lg font-black flex items-center justify-center flex-shrink-0">
              {(p.name || '?').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-black truncate" data-testid="crm-name">{p.name || data.bookings?.[0]?.client_name || data.quotes?.[0]?.client_name || '(Unnamed)'}</h2>
                {p.tier && <span className="text-[9px] uppercase bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold flex items-center gap-1"><Crown className="h-3 w-3" />{p.tier}</span>}
                {(p.tags || []).map(t => (
                  <span key={t} className="text-[9px] uppercase bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                    {t}<button onClick={() => removeTag(t)} className="text-gray-400 hover:text-red-600"><X className="h-2.5 w-2.5" /></button>
                  </span>
                ))}
                <span className="flex items-center gap-1">
                  <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTag()} placeholder="+ tag" className="text-[10px] border border-dashed border-gray-300 rounded-full px-2 py-0.5 w-16 focus:w-24 transition-all outline-none" data-testid="crm-tag-input" />
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                {p.phone && <a href={`tel:${p.phone}`} className="flex items-center gap-1 hover:text-red-600"><Phone className="h-3 w-3" />{p.phone}</a>}
                {p.email && <a href={`mailto:${p.email}`} className="flex items-center gap-1 hover:text-red-600"><Mail className="h-3 w-3" />{p.email}</a>}
                {p.company && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{p.company}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm" variant="outline" className="h-8 text-xs"
              data-testid="crm-statement-pdf-btn"
              onClick={() => window.open(`${API}/api/command-center/clients/360/${encodeURIComponent(clientKey)}/statement.pdf`, '_blank')}
            ><FileDown className="h-3 w-3 mr-1" />Statement</Button>
            <Button
              size="sm" className="h-8 text-xs bg-black hover:bg-gray-800 text-white"
              disabled={statementSending || !p.email}
              title={p.email ? `Email statement to ${p.email}` : 'Add an email to the profile first'}
              data-testid="crm-statement-email-btn"
              onClick={emailStatement}
            >{statementSending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}Email Statement</Button>
            <Button size="sm" variant="outline" onClick={editing ? () => setEditing(false) : startEdit} className="h-8 text-xs" data-testid="crm-edit-btn">
              {editing ? <><X className="h-3 w-3 mr-1" />Cancel</> : <><Pencil className="h-3 w-3 mr-1" />Edit Profile</>}
            </Button>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-4">
          {[
            ['Lifetime Value', fmt$(s.lifetime_value || 0), false],
            ['Bookings', s.total_bookings || 0, false],
            ['Upcoming', s.upcoming_bookings || 0, false],
            ['Balance', fmt$(s.ledger_balance || 0), (s.ledger_balance || 0) > 0],
            ['Unpaid Invoices', s.invoices_unpaid || 0, (s.invoices_unpaid || 0) > 0],
          ].map(([l, v, warn]) => (
            <div key={l} className={`border p-2 text-center ${warn ? 'border-red-200 bg-red-50' : 'border-gray-100 bg-gray-50'}`}>
              <p className={`text-base font-black ${warn ? 'text-red-600' : 'text-black'}`} data-testid={`crm-stat-${String(l).toLowerCase().replace(/\s/g, '-')}`}>{v}</p>
              <p className="text-[9px] uppercase tracking-wider text-gray-400 font-bold">{l}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Edit form */}
      {editing && (
        <div className="bg-white border border-gray-200 p-4 mb-4" data-testid="crm-edit-form">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Edit Profile & Preferences</p>
          <div className="grid sm:grid-cols-3 gap-2">
            <Input placeholder="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-9 text-xs" data-testid="crm-edit-name" />
            <Input placeholder="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="h-9 text-xs" />
            <Input placeholder="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="h-9 text-xs" />
            <Input placeholder="Company" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} className="h-9 text-xs" />
            <select value={form.tier} onChange={e => setForm(f => ({ ...f, tier: e.target.value }))} className="border rounded h-9 text-xs px-2">
              <option value="">No tier</option><option value="member">Member</option><option value="vip">VIP</option><option value="black">Black</option><option value="corporate">Corporate</option>
            </select>
            <Input placeholder="Preferred drink" value={form.preferences?.drink} onChange={e => setForm(f => ({ ...f, preferences: { ...f.preferences, drink: e.target.value } }))} className="h-9 text-xs" />
            <Input placeholder="Music preference" value={form.preferences?.music} onChange={e => setForm(f => ({ ...f, preferences: { ...f.preferences, music: e.target.value } }))} className="h-9 text-xs" />
            <Input placeholder="Allergies / avoid" value={form.preferences?.allergies} onChange={e => setForm(f => ({ ...f, preferences: { ...f.preferences, allergies: e.target.value } }))} className="h-9 text-xs" />
            <Input placeholder="Seating / temperature" value={form.preferences?.seating} onChange={e => setForm(f => ({ ...f, preferences: { ...f.preferences, seating: e.target.value } }))} className="h-9 text-xs" />
            <Textarea placeholder="Internal notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="text-xs sm:col-span-3" />
          </div>
          <Button size="sm" onClick={saveEdit} className="bg-red-600 hover:bg-red-700 h-8 text-xs mt-2" data-testid="crm-edit-save"><Check className="h-3 w-3 mr-1" />Save</Button>
        </div>
      )}

      {/* Booking Database — every booking is its own editable record with linked docs */}
      <BookingDatabase data={data} reload={reload} />

      {/* 3-column grid */}
      <div className="grid xl:grid-cols-3 gap-4 items-start">
        {/* Timeline (col span 2) */}
        <div className="xl:col-span-2 bg-white border border-gray-200" data-testid="crm-timeline">
          <div className="p-3 border-b border-gray-100">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Activity — log a note or text the client</p>
            <div className="flex gap-2">
              <Textarea value={composer} onChange={e => setComposer(e.target.value)} placeholder={p.phone ? `Note or SMS to ${p.phone}…` : 'Log a note…'} rows={2} className="text-sm flex-1" data-testid="crm-composer" />
              <div className="flex flex-col gap-1">
                <Button size="sm" onClick={logNote} disabled={!composer.trim()} variant="outline" className="h-8 text-xs" data-testid="crm-log-note"><StickyNote className="h-3 w-3 mr-1" />Note</Button>
                {p.phone && <Button size="sm" onClick={sendSms} disabled={sendingSms || !composer.trim()} className="bg-red-600 hover:bg-red-700 h-8 text-xs" data-testid="crm-send-sms"><Send className="h-3 w-3 mr-1" />SMS</Button>}
              </div>
            </div>
          </div>
          <div className="max-h-[520px] overflow-y-auto p-3">
            {timeline.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-6">No activity yet — bookings, quotes, texts, notes and payments will appear here.</p>
            ) : timeline.map((t, i) => {
              const meta = TL_META[t.type];
              return (
                <div key={i} className="flex gap-2.5 pb-3 mb-3 border-b border-gray-50 last:border-0" data-testid={`crm-tl-${t.type}-${i}`}>
                  <div className={`h-7 w-7 rounded-full border flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                    <meta.icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-bold truncate">{t.title}</p>
                      <span className="text-[9px] text-gray-400 font-mono flex-shrink-0">{String(t.date).slice(0, 10)}</span>
                    </div>
                    {t.sub && <p className="text-[10px] text-gray-500 truncate">{t.sub}</p>}
                    {t.badge && <span className="text-[8px] uppercase tracking-wider text-gray-400 font-bold">{meta.label} · {t.badge}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right column: ledger + upcoming + prefs */}
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 p-3" data-testid="crm-ledger">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Balance & Ledger</p>
            <div className={`border p-2.5 text-center mb-2 ${(s.ledger_balance || 0) > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
              <p className={`text-xl font-black ${(s.ledger_balance || 0) > 0 ? 'text-red-600' : 'text-green-700'}`} data-testid="crm-balance">{fmt$(s.ledger_balance || 0)}</p>
              <p className="text-[9px] text-gray-400 uppercase tracking-wider">{(s.ledger_balance || 0) > 0 ? 'Owed by client' : 'Settled'}</p>
            </div>
            <div className="grid grid-cols-3 gap-1.5 mb-2">
              <select value={ledgerForm.type} onChange={e => setLedgerForm(f => ({ ...f, type: e.target.value }))} className="border rounded h-8 text-[10px] px-1" data-testid="crm-ledger-type">
                <option value="payment">Payment</option><option value="charge">Charge</option><option value="adjustment">Adjust</option><option value="refund">Refund</option>
              </select>
              <Input type="number" placeholder="$" value={ledgerForm.amount} onChange={e => setLedgerForm(f => ({ ...f, amount: e.target.value }))} className="h-8 text-[10px]" data-testid="crm-ledger-amount" />
              <Button size="sm" onClick={addLedger} disabled={!ledgerForm.amount} className="bg-black hover:bg-gray-800 h-8 text-[10px]" data-testid="crm-ledger-add">Add</Button>
            </div>
            <Input placeholder="Description (Zelle, cash, tolls…)" value={ledgerForm.description} onChange={e => setLedgerForm(f => ({ ...f, description: e.target.value }))} className="h-8 text-[10px] mb-2" />
            <div className="max-h-[160px] overflow-y-auto space-y-1">
              {(data.ledger || []).slice(0, 15).map(e => (
                <div key={e.entry_id} className="flex justify-between text-[10px] border-b border-gray-50 py-1">
                  <span className="truncate text-gray-600">{e.description || e.type}</span>
                  <span className={`font-mono font-bold flex-shrink-0 ${e.type === 'payment' ? 'text-green-700' : 'text-red-600'}`}>{e.type === 'payment' ? '−' : '+'}{fmt$(e.amount)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-gray-200 p-3" data-testid="crm-upcoming">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Upcoming Trips</p>
            {upcoming.length === 0 ? <p className="text-[10px] text-gray-400">None scheduled.</p> : upcoming.map(b => (
              <div key={b.booking_id} className="border border-gray-100 p-2 mb-1.5 text-[10px]">
                <div className="flex justify-between font-bold text-[11px]"><span>{b.event_date}</span><span className="font-mono">{fmt$(b.total)}</span></div>
                <p className="text-gray-500 truncate">{b.vehicle} {b.pickup_time ? `· ${b.pickup_time}` : ''} {b.flight_number ? <span className="text-blue-700">· <Plane className="h-2.5 w-2.5 inline" /> {b.flight_number}</span> : ''}</p>
              </div>
            ))}
          </div>

          <DocumentsPanel data={data} reload={reload} />

          <div className="bg-white border border-gray-200 p-3" data-testid="crm-files">
            <AttachmentsSection entityType="client" entityId={p.client_id || clientKey} title="Client Files" uploadedBy="admin" />
          </div>

          {(hasPrefs || p.notes) && (
            <div className="bg-white border border-gray-200 p-3" data-testid="crm-prefs">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Preferences & Notes</p>
              {prefs.drink && <p className="text-[11px]"><b>Drink:</b> {prefs.drink}</p>}
              {prefs.music && <p className="text-[11px]"><b>Music:</b> {prefs.music}</p>}
              {prefs.allergies && <p className="text-[11px]"><b>Allergies:</b> {prefs.allergies}</p>}
              {prefs.seating && <p className="text-[11px]"><b>Seating:</b> {prefs.seating}</p>}
              {p.notes && <p className="text-[11px] text-gray-500 mt-1 border-t border-gray-50 pt-1">{p.notes}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
